(() => {
  "use strict";

  const CONFIG_SELECTOR = "[data-blend-webmcp-config]";
  const CANONICAL_ORIGIN = "https://blendcommerce.com";
  const REVIEW_SOURCE_HOSTS = new Set([
    "www.reviews.io",
    "www.shopify.com",
    "www.google.com",
    "clutch.co",
  ]);
  const AWARD_EVIDENCE_HOSTS = new Set([
    "globalagencyawards.net",
    "europeanagencyawards.com",
    "ukdigitalexcellenceawards.co.uk",
    "nationaldigitalawards.co.uk",
    "globaldigitalexcellenceawards.com",
    "ukecommerceawards.co.uk",
    "ukagencyawards.co",
    "app.qwoted.com",
    "www.bluearray.co.uk",
    "www.uknewsgroup.co.uk",
    "britishbusinessexcellenceawards.co.uk",
  ]);
  const FETCH_TIMEOUT_MS = 5000;
  const CONTENT_FETCH_TIMEOUT_MS = 7000;
  const MAX_INDEX_BYTES = 1_000_000;
  const MAX_PAGE_BYTES = 2_000_000;
  const MAX_CONTENT_TEXT = 12_000;
  const MAX_HEADINGS = 30;
  const MAX_QUERY_LENGTH = 240;
  const MAX_RESULTS = 10;
  const STATE_KEY = Symbol.for("blend.webmcp.state");
  const VERSION = "2026-09-28.6";
  const JSON_SCHEMA = "https://json-schema.org/draft/2020-12/schema";
  const CONTENT_TYPES = [
    "page",
    "article",
    "article_library",
    "newsletter",
    "newsletter_library",
    "case_study",
    "case_study_library",
    "ab_test",
    "ab_test_library",
    "resource",
    "resource_library",
  ];
  const BUYER_JOB_IDS = Array.from(
    { length: 24 },
    (_, index) => `JTBD-${String(index + 1).padStart(2, "0")}`,
  );
  const BUYER_JOB_KEYS = new Set([
    "candidate_service_ids",
    "family",
    "id",
    "name",
    "prompt_ids",
    "webmcp_routes",
  ]);
  const BUYER_PROMPT_KEYS = new Set([
    "awareness_stage",
    "buyer_roles",
    "conversational_prompt",
    "desired_progress",
    "id",
    "job_id",
    "prompt_mode",
    "search_query_variant",
    "trigger",
  ]);
  const STOPWORDS = new Set([
    "a",
    "about",
    "an",
    "and",
    "are",
    "can",
    "could",
    "do",
    "for",
    "from",
    "how",
    "i",
    "in",
    "is",
    "it",
    "my",
    "of",
    "on",
    "our",
    "shopify",
    "that",
    "the",
    "this",
    "to",
    "we",
    "what",
    "when",
    "should",
    "would",
    "with",
  ]);
  const BUYER_JOB_HINTS = [
    ["JTBD-01", /\b(launch|relaunch|scale paid|before scaling|pre.launch)\b/i, 120],
    ["JTBD-02", /old theme|aging theme|need.*redesign|signs.*redesign/i, 120],
    ["JTBD-03", /traffic.*(no|few|hardly).*sales|visitors.*not.*buy|traffic.*not convert/i, 150],
    ["JTBD-04", /(sales|conversion).*(sudden|suddenly|dropped|fell)|drop.*no changes/i, 160],
    ["JTBD-05", /bot traffic|data integrity|tracking|analytics|ga4|real conversion/i, 130],
    ["JTBD-06", /root cause|price.*trust|trust.*design|isolate.*cause|what.*causing/i, 130],
    ["JTBD-07", /product page|\bpdp\b|add to cart.*product/i, 130],
    ["JTBD-08", /add.*cart.*(checkout|progress)|cart.*block|cart abandonment/i, 140],
    ["JTBD-09", /reach.*checkout|checkout.*(abandon|complete|drop)|payment.*shipping/i, 140],
    ["JTBD-10", /mobile.*(conversion|desktop|journey)|desktop.*mobile/i, 140],
    ["JTBD-11", /search|navigation|filters?|product discovery|collection page/i, 110],
    ["JTBD-12", /redesign.*(research|approve|govern|customer problem)|migration/i, 120],
    ["JTBD-13", /redesign.*(worse|drop|fail|recover)|post.redesign/i, 180],
    ["JTBD-14", /\baov\b.*profit|profit.*order|bundle|upsell|basket value/i, 130],
    ["JTBD-15", /which matters more|commercial metrics?|lifetime value|contribution margin|revenue per visitor/i, 120],
    ["JTBD-16", /what.*test next|experiment roadmap|a\/b test|hypothesis|usual conversion tips/i, 140],
    ["JTBD-17", /marketplace|amazon.*own store|own store.*marketplace|\bdtc\b/i, 150],
    ["JTBD-18", /theme.*update.*test|app.*update.*test|release.*qa|quality assurance|before.*traffic/i, 140],
    ["JTBD-19", /hire.*agency|outside.*help|in.house|buy.*build|team.*fix.*basics/i, 170],
    ["JTBD-20", /proof.*specialist|shortlist|provider fit|awards?|reviews?.*agency/i, 130],
    ["JTBD-21", /same team.*(find|build)|integrated.*delivery|build.*fixes/i, 140],
    ["JTBD-22", /agency.*(failed|disappoint|no improve|did not improve)|provider.*(failed|wrong)|changes.*sales.*not/i, 180],
    ["JTBD-23", /measurable value|attribution|did.*change.*work|baseline.*comparison/i, 140],
    ["JTBD-24", /single.item|bundles?.*discount|merchandis|assortment|choice clarity/i, 140],
  ];

  const state = window[STATE_KEY] || {
    index: null,
    registered: new Set(),
    registrationInProgress: false,
    registrationController: null,
    lifecycleBound: false,
    retryTimer: null,
    active: true,
  };
  window[STATE_KEY] = state;

  const plainObject = (value) =>
    Boolean(value) && typeof value === "object" && !Array.isArray(value);

  const hasExactKeys = (value, allowedKeys) => {
    const keys = Object.keys(value);
    return keys.length === allowedKeys.size && keys.every((key) => allowedKeys.has(key));
  };

  const cleanString = (value, maximum = MAX_QUERY_LENGTH) => {
    if (value == null) return "";
    if (typeof value !== "string") throw new TypeError("Expected a string.");
    return value.trim().slice(0, maximum);
  };

  const optionalNumber = (value, fieldName) => {
    if (value == null || value === "") return null;
    const number = Number(value);
    if (!Number.isFinite(number) || number < 0) {
      throw new TypeError(`${fieldName} must be a non-negative number.`);
    }
    return number;
  };

  const boundedLimit = (value, fallback, maximum) => {
    if (value == null) return fallback;
    const number = Number(value);
    if (!Number.isInteger(number) || number < 1 || number > maximum) {
      throw new TypeError(`limit must be between 1 and ${maximum}.`);
    }
    return number;
  };

  const normaliseText = (value) =>
    String(value || "")
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/\bf\s*&\s*b\b/g, " food beverage ")
      .replace(/\bfood\s*(?:&|and)?\s*(?:beverages?|drinks?)\b/g, " food beverage ")
      .replace(/\bpdps?\b/g, " product page ")
      .replace(/[^a-z0-9+%]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const queryTokens = (query) =>
    [...new Set(
      normaliseText(cleanString(query))
        .split(" ")
        .filter((token) => token.length > 1 && !STOPWORDS.has(token)),
    )].slice(0, 12);

  const canonicalUrl = (value) => {
    if (typeof value !== "string" || value.length > 600) {
      throw new TypeError("Invalid canonical URL.");
    }
    const url = new URL(value);
    if (
      url.origin !== CANONICAL_ORIGIN ||
      url.username ||
      url.password ||
      url.protocol !== "https:"
    ) {
      throw new TypeError("Index URLs must use the Blend Commerce origin.");
    }
    return url.href;
  };

  const allowlistedPublicUrl = (value, allowedHosts) => {
    if (typeof value !== "string" || value.length > 4000) {
      throw new TypeError("Invalid public evidence URL.");
    }
    const url = new URL(value);
    if (
      url.protocol !== "https:" ||
      !allowedHosts.has(url.hostname) ||
      url.username ||
      url.password
    ) {
      throw new TypeError("Public evidence URL is not allowlisted.");
    }
    return url.href;
  };

  const validateString = (value, fieldName, maximum, allowNull = false) => {
    if (allowNull && value == null) return;
    if (typeof value !== "string" || !value.trim() || value.length > maximum) {
      throw new TypeError(`Invalid ${fieldName}.`);
    }
  };

  const validateIndex = (index) => {
    if (!plainObject(index) || index.schema_version !== "1.0.0") {
      throw new TypeError("Unsupported WebMCP index schema.");
    }
    if (index.canonical_origin !== CANONICAL_ORIGIN) {
      throw new TypeError("Unexpected index origin.");
    }
    for (const key of [
      "content",
      "reviews",
      "awards",
      "services",
      "frameworks",
      "buyer_jobs",
      "buyer_prompts",
    ]) {
      if (!Array.isArray(index[key])) throw new TypeError(`Missing ${key} array.`);
    }
    if (!plainObject(index.business_rules)) {
      throw new TypeError("Missing business rules.");
    }
    if (
      !plainObject(index.buyer_prompt_scope) ||
      index.buyer_prompt_scope.source_version !== "v3-researched" ||
      index.buyer_prompt_scope.research_market !== "United States" ||
      index.buyer_prompt_scope.research_language !== "English"
    ) {
      throw new TypeError("Invalid Buyer Job research scope.");
    }
    if (index.content.length > 5000 || index.reviews.length > 1000) {
      throw new TypeError("Index exceeds the approved record boundary.");
    }

    for (const item of index.content) {
      if (!plainObject(item) || !CONTENT_TYPES.includes(item.type)) {
        throw new TypeError("Invalid content record.");
      }
      validateString(item.id, "content id", 300);
      validateString(item.title, "content title", 500);
      if (item.industries != null && (!Array.isArray(item.industries) || item.industries.length > 8 || item.industries.some((industry) => typeof industry !== "string" || !industry.trim() || industry.length > 100))) throw new TypeError("Invalid content industries.");
      if (item.search_aliases != null && (!Array.isArray(item.search_aliases) || item.search_aliases.length > 10 || item.search_aliases.some((alias) => typeof alias !== 'string' || alias.length > 100))) throw new TypeError('Invalid content search aliases.');
      if (/^untitled$/i.test(item.title.trim())) {
        throw new TypeError("Content record has no usable title.");
      }
      if (item.summary != null) {
        validateString(item.summary, "content summary", 700);
        if (/<[^>]+>/.test(item.summary)) {
          throw new TypeError("Content summary contains HTML.");
        }
      }
      if (item.type === "case_study") {
        validateString(item.client_name, "case-study client name", 200);
        if (!normaliseText(item.title).includes(normaliseText(item.client_name))) {
          throw new TypeError("Case-study title must identify its client.");
        }
      }
      canonicalUrl(item.canonical_url);
      if (
        !Array.isArray(item.buyer_job_ids) ||
        item.buyer_job_ids.some((id) => !BUYER_JOB_IDS.includes(id))
      ) {
        throw new TypeError("Invalid buyer-job mapping.");
      }
    }

    for (const item of index.reviews) {
      if (!plainObject(item)) throw new TypeError("Invalid review record.");
      validateString(item.id, "review id", 300);
      validateString(item.source, "review source", 100);
      validateString(item.text, "review text", 5000);
      canonicalUrl(item.canonical_url);
      allowlistedPublicUrl(item.source_url, REVIEW_SOURCE_HOSTS);
    }

    for (const item of index.awards) {
      if (
        !plainObject(item) ||
        !["winner", "finalist_only", "current_finalist"].includes(item.outcome)
      ) {
        throw new TypeError("Invalid award record.");
      }
      validateString(item.id, "award id", 100);
      validateString(item.programme, "award programme", 300);
      validateString(item.category, "award category", 500);
      canonicalUrl(item.canonical_url);
      if (!Array.isArray(item.evidence_urls) || !item.evidence_urls.length) {
        throw new TypeError("Award record has no public evidence.");
      }
      for (const value of item.evidence_urls) {
        allowlistedPublicUrl(value, AWARD_EVIDENCE_HOSTS);
      }
    }

    for (const item of index.services) {
      if (!plainObject(item) || !["core", "adjacent"].includes(item.tier)) {
        throw new TypeError("Invalid service record.");
      }
      validateString(item.id, "service id", 100);
      validateString(item.title, "service title", 300);
      validateString(item.summary, "service summary", 1500);
      canonicalUrl(item.canonical_url);
    }

    for (const item of index.frameworks) {
      if (!plainObject(item) || !Array.isArray(item.components)) {
        throw new TypeError("Invalid framework record.");
      }
      validateString(item.id, "framework id", 100);
      validateString(item.title, "framework title", 300);
      canonicalUrl(item.canonical_url);
    }
    if (index.buyer_jobs.length !== 24 || index.buyer_prompts.length !== 96) {
      throw new TypeError("Unexpected Buyer Job universe size.");
    }
    const serviceIds = new Set(index.services.map((service) => service.id));
    const jobIds = new Set();
    for (const job of index.buyer_jobs) {
      if (!plainObject(job) || !hasExactKeys(job, BUYER_JOB_KEYS)) {
        throw new TypeError("Invalid Buyer Job record.");
      }
      validateString(job.id, "Buyer Job id", 20);
      validateString(job.name, "Buyer Job name", 120);
      validateString(job.family, "Buyer Job family", 120);
      if (
        !BUYER_JOB_IDS.includes(job.id) ||
        jobIds.has(job.id) ||
        !Array.isArray(job.prompt_ids) ||
        !Array.isArray(job.webmcp_routes) ||
        !Array.isArray(job.candidate_service_ids) ||
        job.candidate_service_ids.some((id) => !serviceIds.has(id))
      ) {
        throw new TypeError("Invalid Buyer Job mapping.");
      }
      jobIds.add(job.id);
    }
    const promptIds = new Set();
    for (const prompt of index.buyer_prompts) {
      if (
        !plainObject(prompt) ||
        !hasExactKeys(prompt, BUYER_PROMPT_KEYS) ||
        !jobIds.has(prompt.job_id) ||
        promptIds.has(prompt.id)
      ) {
        throw new TypeError("Invalid Buyer Job prompt.");
      }
      validateString(prompt.id, "Buyer Job prompt id", 20);
      validateString(prompt.conversational_prompt, "conversational prompt", 800);
      validateString(prompt.search_query_variant, "search query variant", 500);
      validateString(prompt.trigger, "Buyer Job trigger", 500);
      validateString(prompt.desired_progress, "Buyer Job desired progress", 500);
      promptIds.add(prompt.id);
    }
    return index;
  };

  const indexAssetUrl = (config) => {
    const raw = config?.dataset?.indexUrl;
    if (!raw) throw new TypeError("Missing WebMCP index URL.");
    const url = new URL(raw, window.location.origin);
    const sameOriginAsset = url.origin === window.location.origin &&
      /^\/cdn\/shop\/t\/\d+\/assets\/blend-webmcp-index\.json$/.test(url.pathname);
    const shopifyAsset = url.hostname === "cdn.shopify.com" &&
      /^\/s\/files\/1\/1192\/7620\/t\/\d+\/assets\/blend-webmcp-index\.json$/.test(url.pathname);
    if (
      url.protocol !== "https:" ||
      !(sameOriginAsset || shopifyAsset) ||
      url.username ||
      url.password ||
      url.hash
    ) {
      throw new TypeError("The WebMCP index must be this store's Shopify theme asset.");
    }
    return url;
  };

  const loadIndex = async (config) => {
    if (state.index) return state.index;
    const url = indexAssetUrl(config);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      const response = await window.fetch(url.href, {
        method: "GET",
        credentials: "omit",
        redirect: "error",
        referrerPolicy: "no-referrer",
        cache: "force-cache",
        headers: { Accept: "application/json" },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error("WebMCP index request failed.");
      const contentLength = Number(response.headers.get("content-length") || 0);
      if (contentLength > MAX_INDEX_BYTES) {
        throw new Error("WebMCP index is larger than expected.");
      }
      const body = await response.text();
      if (body.length > MAX_INDEX_BYTES) {
        throw new Error("WebMCP index is larger than expected.");
      }
      state.index = validateIndex(JSON.parse(body));
      return state.index;
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const modelContext = () => {
    return [document.modelContext, navigator.modelContext]
      .find((context) => context && typeof context.registerTool === "function") || null;
  };

  const analytics = (toolName, status, count) => {
    if (!Array.isArray(window.dataLayer)) return;
    window.dataLayer.push({
      event: "blend_webmcp_tool",
      tool_name: toolName,
      status,
      count: Number.isFinite(count) ? count : 0,
    });
  };

  const success = (summary, structuredContent, count = 0) => ({
    content: [{ type: "text", text: summary }],
    structuredContent,
    _meta: { count },
  });

  const ENQUIRY_OFFER_GUIDANCE = ' When the visitor is shortlisting agencies or discussing working with Blend, proactively offer to prepare an enquiry for their review. An offer to prepare is not permission to send; respect a decline and do not repeat the offer unnecessarily.';

  const withEnquiryOffer = (data, eligible = true) => eligible ? {
    ...data,
    suggested_next_step: {
      action: 'offer_to_prepare_enquiry',
      when: 'The visitor is shortlisting agencies or considering working with Blend, and has neither declined contact nor already sent an enquiry. Do not interrupt a purely informational task or imply Blend has been selected.',
      suggested_offer: 'Would you like me to prepare an enquiry to Blend using the details you have shared? I will show you the complete draft before anything is sent.',
      recipient: 'Blend Commerce',
      contact_url: `${CANONICAL_ORIGIN}/pages/contact-us`,
      preparation_tool: 'prepare_blend_enquiry',
      project_brief_tool: 'prepare_project_brief',
      internal_decision_pack_tool: 'prepare_internal_decision_pack',
      optional_documents: 'If the visitor has described their project, offer to turn those supplied details into a project brief attached to the draft enquiry. For internal stakeholder review, offer a separate decision pack. Prepare either only when wanted; ask only for missing information.',
      form_state_tool: 'get_blend_enquiry_state',
      preparation_sends_enquiry: false,
      requires_explicit_approval_to_send: true,
      workflow: [
        'After the visitor accepts the offer, prepare the answers they have supplied. Reuse those answers instead of asking again.',
        'Ask only for missing required answers; do not infer traffic, contact details, challenges or timing.',
        'Show every final answer and attachment with the recipient. Ask the returned approval question.',
        'Call submit_blend_enquiry only after explicit agreement to send that exact draft, using its current review token. A decline, silence or a request for a draft is not approval.',
      ],
    },
  } : data;

  const failure = (error) => ({
    isError: true,
    content: [
      {
        type: "text",
        text: error instanceof TypeError ? error.message : "The tool could not complete this request. Please retry or use the visible site.",
      },
    ],
    structuredContent: {
      error: error instanceof TypeError ? "invalid_request" : "temporarily_unavailable",
      next_step: error instanceof TypeError ? "Check the tool schema and supplied fields." : "Retry later or use the visible site. Do not assume the action succeeded.",
    },
  });

  const validateArguments = (value, schema, field = "arguments") => {
    if (schema.enum && !schema.enum.includes(value)) throw new TypeError(`Unsupported ${field}.`);
    if (schema.const !== undefined && value !== schema.const) throw new TypeError(`Invalid ${field}.`);
    if (schema.type === "object") {
      if (!plainObject(value)) throw new TypeError(`${field} must be an object.`);
      for (const key of schema.required || []) {
        if (!Object.prototype.hasOwnProperty.call(value, key)) throw new TypeError(`${key} is required.`);
      }
      for (const [key, item] of Object.entries(value)) {
        if (!Object.prototype.hasOwnProperty.call(schema.properties, key)) throw new TypeError(`Unsupported field: ${key}.`);
        validateArguments(item, schema.properties[key], key);
      }
    } else if (schema.type === "array") {
      if (!Array.isArray(value) || value.length > (schema.maxItems ?? Infinity) || value.length < (schema.minItems ?? 0)) throw new TypeError(`Invalid ${field} array.`);
      value.forEach((item) => validateArguments(item, schema.items, field));
      if (schema.uniqueItems && new Set(value).size !== value.length) throw new TypeError(`${field} must not contain duplicates.`);
    } else if (schema.type === "string") {
      if (typeof value !== "string" || value.length > (schema.maxLength ?? Infinity) || value.trim().length < (schema.minLength ?? 0)) throw new TypeError(`Invalid ${field} text length.`);
      if (schema.pattern && !new RegExp(schema.pattern).test(value)) throw new TypeError(`Invalid ${field} format.`);
    } else if (schema.type === "number" || schema.type === "integer") {
      if (typeof value !== "number" || !Number.isFinite(value) || (schema.type === "integer" && !Number.isInteger(value)) || value < (schema.minimum ?? -Infinity) || value > (schema.maximum ?? Infinity)) throw new TypeError(`Invalid ${field} number.`);
    } else if (schema.type === "boolean" && typeof value !== "boolean") throw new TypeError(`${field} must be a boolean.`);
  };

  const wrapped = (definition) => async (rawArguments, options = {}) => {
    const { name, execute, inputSchema } = definition;
    try {
      const args = rawArguments == null ? {} : rawArguments;
      validateArguments(args, inputSchema);
      if (options.signal?.aborted) throw new DOMException("Tool cancelled.", "AbortError");
      const result = await execute(args, options);
      if (state.index && definition.annotations?.readOnlyHint && name !== 'get_blend_enquiry_state') {
        result._meta = { ...result._meta, index_updated_at: state.index.generated_at, evidence_scope: state.index.evidence_scope };
      }
      const count = Number(result?._meta?.count || 0);
      analytics(name, result.isError ? "error" : "ok", count);
      return result;
    } catch (error) {
      if (error.name === "AbortError") throw error;
      analytics(name, "error", 0);
      return failure(error);
    }
  };

  const searchableText = (item, fields) =>
    normaliseText([...fields.map((field) => item[field]), ...(item.search_aliases || []), ...(item.industries || [])].filter(Boolean).join(" "));

  const score = (item, tokens, fields) => {
    if (!tokens.length) return { relevance: 1, matchedTokens: 0 };
    const title = normaliseText(
      item.title || item.category || item.reviewer_name || "",
    );
    const haystack = searchableText(item, fields);
    let relevance = 0;
    let matchedTokens = 0;
    for (const token of tokens) {
      let matched = false;
      if (title === token) {
        relevance += 14;
        matched = true;
      } else if (title.startsWith(token)) {
        relevance += 8;
        matched = true;
      } else if (title.includes(token)) {
        relevance += 5;
        matched = true;
      }
      if (haystack.includes(token)) {
        relevance += 2;
        matched = true;
      }
      if (matched) matchedTokens += 1;
    }
    if ((item.search_aliases || []).some((alias) => normaliseText(alias) === tokens.join(' '))) relevance += 50;
    return { relevance, matchedTokens };
  };

  const ranked = (items, query, fields, limit, relaxed = false) => {
    const tokens = queryTokens(query);
    const requiredTokenMatches = tokens.length
      ? Math.max(1, Math.ceil(tokens.length * 0.6))
      : 0;
    return items
      .map((item) => ({ item, ...score(item, tokens, fields) }))
      .filter(
        ({ relevance, matchedTokens }) =>
          !tokens.length ||
          (relevance > 0 && matchedTokens >= (relaxed ? 1 : requiredTokenMatches)),
      )
      .sort(
        (a, b) =>
          b.relevance - a.relevance ||
          String(b.item.last_modified || b.item.date || b.item.year || "").localeCompare(
            String(a.item.last_modified || a.item.date || a.item.year || ""),
          ),
      )
      .slice(0, limit)
      .map(({ item }) => item);
  };

  const compactContent = (item) => ({
    id: item.id,
    type: item.type,
    title: item.title,
    client_name: item.client_name || null,
    industries: item.industries || [],
    summary: item.summary || null,
    url: item.canonical_url,
    last_modified: item.last_modified,
    buyer_job_ids: item.buyer_job_ids,
  });

  const typedQuery = (query, type) => type === "case_study"
    ? query.replace(/\bcase[\s-]+stud(?:y|ies)\b/gi, " ").trim()
    : query;

  const findContent = (index) => async (args) => {
    const type = args.type == null ? null : cleanString(args.type, 40);
    const query = typedQuery(cleanString(args.query), type);
    const buyerJobId =
      args.buyer_job_id == null ? null : cleanString(args.buyer_job_id, 20);
    const limit = boundedLimit(args.limit, 5, 8);
    if (type && !CONTENT_TYPES.includes(type)) throw new TypeError("Invalid type.");
    if (buyerJobId && !BUYER_JOB_IDS.includes(buyerJobId)) {
      throw new TypeError("Invalid buyer job.");
    }
    let records = index.content;
    if (type) records = records.filter((item) => item.type === type);
    if (buyerJobId) {
      records = records.filter((item) => item.buyer_job_ids.includes(buyerJobId));
    }
    const results = ranked(
      records,
      query,
      [
        "title",
        "client_name",
        "summary",
        "type",
        "canonical_url",
        "buyer_job_ids",
      ],
      limit,
    ).map(compactContent);
    return success(
      results.length
        ? `Found ${results.length} approved Blend Commerce content record${results.length === 1 ? "" : "s"}.`
        : "No approved Blend Commerce content matched the request.",
      withEnquiryOffer({ count: results.length, results }, type === 'case_study' && results.length > 0),
      results.length,
    );
  };

  const findTypedContent = (index, type, label, maximum = 6) => async (args) => {
    const query = typedQuery([args.query, args.problem, args.metric, args.topic]
      .filter(Boolean)
      .map((value) => cleanString(value))
      .join(" "), type);
    const limit = boundedLimit(args.limit, 5, maximum);
    const productPageIntent = /\b(product\s+(?:detail\s+)?pages?|pdps?)\b/i.test(
      query,
    );
    let records = index.content.filter((item) => item.type === type);
    if (productPageIntent) {
      records = records.filter((item) =>
        normaliseText(`${item.title} ${item.summary || ""}`).includes(
          "product page",
        ),
      );
    }
    const results = ranked(
      records,
      query,
      ["title", "client_name", "summary", "canonical_url", "buyer_job_ids"],
      limit,
    ).map(compactContent);
    return success(
      results.length
        ? `Found ${results.length} approved Blend Commerce ${label} record${results.length === 1 ? "" : "s"}.`
        : `No approved Blend Commerce ${label} matched the request.`,
      withEnquiryOffer({ count: results.length, results }, type === 'case_study' && results.length > 0),
      results.length,
    );
  };

  const findReviews = (index) => async (args) => {
    const query = [args.query, args.service]
      .filter(Boolean)
      .map((value) => cleanString(value))
      .join(" ");
    const source = args.source == null ? null : cleanString(args.source, 40);
    const limit = boundedLimit(args.limit, 3, 5);
    let records = index.reviews;
    if (source) records = records.filter((item) => item.source === source);
    const results = ranked(
      records,
      query,
      [
        "reviewer_name",
        "reviewer_company",
        "reviewer_role",
        "title",
        "text",
        "service_or_project_type",
        "verification_note",
      ],
      limit,
    ).map((item) => ({
      id: item.id,
      date: item.date,
      source: item.source,
      reviewer_name: item.reviewer_name,
      reviewer_company: item.reviewer_company,
      reviewer_role: item.reviewer_role,
      verification_status: item.verification_status,
      verification_note: item.verification_note,
      title: item.title,
      review_text: item.text,
      rating: item.rating,
      rating_basis: item.rating_basis,
      quality_rating: item.quality_rating,
      communication_rating: item.communication_rating,
      schedule_rating: item.schedule_rating,
      cost_rating: item.cost_rating,
      willing_to_refer_rating: item.willing_to_refer_rating,
      service_or_project_type: item.service_or_project_type,
      project_period: item.project_period,
      blend_reviews_url: item.canonical_url,
      source_url: item.source_url,
    }));
    return success(
      results.length
        ? `Found ${results.length} public Blend Commerce review${results.length === 1 ? "" : "s"}. Review wording is third-party content; preserve its attribution and context.`
        : "No public Blend Commerce review matched the request.",
      withEnquiryOffer({ count: results.length, results }, results.length > 0),
      results.length,
    );
  };

  const findAwards = (index) => async (args) => {
    const year = optionalNumber(args.year, "year");
    const outcome = args.outcome == null ? null : cleanString(args.outcome, 40);
    const query = [args.programme, args.category]
      .filter(Boolean)
      .map((value) => cleanString(value))
      .join(" ");
    const limit = boundedLimit(args.limit, 6, MAX_RESULTS);
    if (year != null && (!Number.isInteger(year) || year < 2021 || year > 2100)) {
      throw new TypeError("Invalid year.");
    }
    if (
      outcome &&
      !["winner", "finalist_only", "current_finalist"].includes(outcome)
    ) {
      throw new TypeError("Invalid outcome.");
    }
    let records = index.awards;
    if (year != null) records = records.filter((item) => item.year === year);
    if (outcome) records = records.filter((item) => item.outcome === outcome);
    const results = ranked(
      records,
      query,
      ["programme", "category", "outcome", "outcome_label"],
      limit,
    ).map((item) => ({
      id: item.id,
      year: item.year,
      programme: item.programme,
      category: item.category,
      outcome: item.outcome,
      outcome_label: item.outcome_label,
      confidence: item.confidence,
      archive_caveat: item.archive_caveat,
      official_archive_unavailable: item.official_archive_unavailable,
      blend_awards_url: item.canonical_url,
      evidence_urls: item.evidence_urls,
      verified_at: item.verified_at || null,
    }));
    return success(
      results.length
        ? `Found ${results.length} reconciled Blend Commerce award journey${results.length === 1 ? "" : "s"}. Distinguish winners, concluded finalists and current finalists.`
        : "No reconciled Blend Commerce award matched the request.",
      { count: results.length, results },
      results.length,
    );
  };

  const getService = (index) => async (args) => {
    const serviceId = cleanString(args.service_id, 100);
    const service = index.services.find((item) => item.id === serviceId);
    if (!service) throw new TypeError("Unknown service id.");
    return success(
      `Found the approved ${service.title} service record.`,
      withEnquiryOffer({ service }),
      1,
    );
  };

  // Scope is ordinary language, not a bag of positive service keywords. Keep
  // unknown, denied and background statements out of affirmative requirements.
  const confirmedRequirements = (value, explicitNeed = false) => {
    const clauses = String(value || "").replace(/[’‘]/g, "'")
      .split(/[.!?;\n]+|\b(?:but|however|whereas)\b|\band\s+(?=(?:we\s+)?(?:need|want|require|seek|have|use)\b)|,\s*(?=(?:just|only|we|need|want|seeking|looking)\b)/i);
    return clauses.filter((clause) => {
      if (/\b(?:unknown|unsure|uncertain|undecided|unconfirmed|unavailable|tbd|undisclosed|unprovided)\b|\b(?:not|never|without|neither|nor)\b|\b(?:don'?t|doesn'?t|isn'?t|aren'?t|haven'?t|hasn'?t|won'?t|can'?t|didn'?t)\b|\bno\s+(?:need|plans?|requirement|interest|redesign|rebuild|testing|subscriptions?|recharge|rebuy|quiz|email|klaviyo|sms|maintenance)\b|\byet\s+to\s+(?:confirm|decide|supply|provide)\b|\bto\s+be\s+(?:confirmed|decided|supplied|provided)\b/i.test(clause)) return false;
      if (/\b(?:provided|supplied|confirmed|known|decided|shared)\s*[:=]\s*(?:no|false)\b|\b(?:lack|missing)\b/i.test(clause)) return false;
      if (/\b(?:already\s+(?:use|using|have|installed)|currently\s+(?:use|using)|installed|our\s+(?:current\s+)?(?:app|stack))\b/i.test(clause) && !/\b(?:need|want|require|improve|fix|migrate|optimise|optimize)\b/i.test(clause)) return false;
      if (explicitNeed) return true;
      // An installed app or a commercial metric alone is not a request to buy
      // that integration. Scope needs an affirmative project/action signal.
      return /\b(?:need|needs|want|wants|require|requires|seeking|help|improve|improving|optimise|optimize|optimisation|optimization|fix|audit|diagnose|diagnosis|roadmap|prioriti\w*|implement\w*|develop\w*|build|rebuild|redesign|replatform|migrat\w*|test|testing|experiment\w*)\b|\blooking\s+for\b/i.test(clause);
    }).join(" ");
  };

  const recommendService = (index) => async (args) => {
    const platform = cleanString(args.platform || "Unknown", 40);
    const annualRevenue = optionalNumber(args.annual_revenue_usd, "annual_revenue_usd");
    const monthlySessions = optionalNumber(args.monthly_sessions, "monthly_sessions");
    const wantsAbTesting = args.wants_ab_testing === true;
    const needs = Array.isArray(args.needs)
      ? args.needs.map((value) => cleanString(value, 80)).slice(0, 8)
      : [];
    const projectScope = cleanString(args.project_scope, 500);
    const confirmedNeeds = needs.map((need) => confirmedRequirements(need, true)).filter(Boolean);
    const confirmedScope = confirmedRequirements(projectScope);
    const combined = normaliseText(`${confirmedNeeds.join(" ")} ${confirmedScope}`);
    const serviceIds = [];
    const testingRequested = wantsAbTesting || /\ba b\b|\bexperiment\w*|\btesting\b/.test(combined);

    if (/redesign|new theme|replatform/.test(combined)) {
      serviceIds.push("cro_led_redesign");
    }
    if (/implement|development|build|qa|deploy/.test(combined)) {
      serviceIds.push("cro_implementation");
    }
    if (/audit|insight|diagnos|roadmap|prioriti/.test(combined)) {
      serviceIds.push("cro_insights");
    }
    if (testingRequested) {
      if (monthlySessions != null && monthlySessions >= 50000) {
        serviceIds.push("ab_testing");
      } else {
        serviceIds.push("cro_insights", "cro_implementation");
      }
    }
    if (/maintenance|theme support|technical support/.test(combined)) serviceIds.push("shopify_maintenance");
    if (/recharge|subscription/.test(combined)) serviceIds.push("recharge");
    if (/rebuy/.test(combined)) serviceIds.push("rebuy");
    if (/quiz|guided discovery/.test(combined)) serviceIds.push("onsite_quizzes");
    if (/klaviyo|email|sms/.test(combined)) serviceIds.push("klaviyo");
    if (!serviceIds.length) serviceIds.push("cro_insights");

    const uniqueIds = [...new Set(serviceIds)].slice(0, 3);
    const recommendations = uniqueIds
      .map((id) => index.services.find((service) => service.id === id))
      .filter(Boolean);
    const fitNotes = [];
    let status = "matched";

    if (!/^(shopify|shopify plus|unknown)$/i.test(platform)) {
      status = "clarify";
      fitNotes.push(
        "Blend specialises in Shopify and Shopify Plus; confirm the platform and required Shopify scope before treating this as a match.",
      );
    }
    if (annualRevenue != null && annualRevenue > 3000000) {
      fitNotes.push(
        "Brands above US$3m annual revenue typically see the strongest fit with Blend's main services.",
      );
    } else if (annualRevenue != null && annualRevenue >= 1000000) {
      fitNotes.push(
        "Blend can work with brands around US$1m annual revenue when the evidence, traffic and scope are appropriate.",
      );
    } else if (annualRevenue != null) {
      status = "clarify";
      fitNotes.push(
        "Revenue alone does not decide fit; a smaller focused micro-engagement may be more appropriate than a main programme.",
      );
    }
    if (testingRequested && monthlySessions != null && monthlySessions < 50000) {
      fitNotes.push(
        "About 50,000 monthly sessions is Blend's practical A/B-testing guideline, so research, implementation or other evidence-led methods may be more useful first.",
      );
    }
    if (testingRequested && monthlySessions == null) {
      status = "clarify";
      fitNotes.push("Monthly traffic has not been supplied. Confirm traffic, conversions and test feasibility before recommending an A/B-testing programme.");
    }
    fitNotes.push(
      `Main services start at US$${Number(index.business_rules.main_service_starting_price_usd).toLocaleString("en-US")}; smaller focused micro-engagements are available and scoped separately.`,
    );

    return success(
      `Preliminary Blend service fit: ${status}. A human should confirm scope before any commitment.`,
      withEnquiryOffer({
        status,
        recommendations,
        fit_notes: fitNotes,
        booking: index.business_rules.booking,
        response_time: index.business_rules.response_time,
        human_confirmation_required: true,
        unconfirmed_scope_ignored: confirmedNeeds.length !== needs.length || normaliseText(confirmedScope) !== normaliseText(projectScope),
      }, status === 'matched' && recommendations.length > 0),
      recommendations.length,
    );
  };

  const diagnosePriority = (index) => async (args) => {
    const knownProblem = cleanString(args.known_problem, 600);
    const conversionSignal = cleanString(args.conversion_rate_signal || "unknown", 20);
    const aovSignal = cleanString(args.aov_signal || "unknown", 20);
    const repeatSignal = cleanString(args.repeat_purchase_signal || "unknown", 20);
    const trafficTrend = cleanString(args.traffic_trend || "unknown", 20);
    const dataConfidence = cleanString(args.data_confidence || "unknown", 20);
    const allowedSignals = new Set(["low", "stable", "high", "unknown"]);
    const allowedTrends = new Set(["down", "stable", "up", "unknown"]);
    const allowedConfidence = new Set(["low", "mixed", "high", "unknown"]);
    if (
      !allowedSignals.has(conversionSignal) ||
      !allowedSignals.has(aovSignal) ||
      !allowedSignals.has(repeatSignal) ||
      !allowedTrends.has(trafficTrend) ||
      !allowedConfidence.has(dataConfidence)
    ) {
      throw new TypeError("Unsupported diagnostic signal.");
    }

    const priorities = [];
    if (dataConfidence === "low" || /tracking|analytics|data|attribution/.test(normaliseText(knownProblem))) {
      priorities.push({
        priority: "Measurement first",
        reason:
          "Resolve metric definitions, tracking and data confidence before attributing an onsite problem or uplift.",
      });
    }
    if (trafficTrend === "down") {
      priorities.push({
        priority: "Separate traffic from conversion",
        reason:
          "A traffic decline can reduce revenue without an onsite conversion problem; diagnose acquisition mix and onsite performance separately.",
      });
    }
    if (conversionSignal === "low") {
      priorities.push({
        priority: "Buy Now",
        reason: "Investigate product discovery and the path from landing to purchase.",
      });
    }
    if (aovSignal === "low") {
      priorities.push({
        priority: "Buy More",
        reason: "Investigate basket building, offers, bundles and merchandising.",
      });
    }
    if (repeatSignal === "low") {
      priorities.push({
        priority: "Buy Again",
        reason: "Investigate repeat purchase, subscriptions, retention and customer value.",
      });
    }
    if (!priorities.length) {
      priorities.push({
        priority: "Clarify the Metric on Fire",
        reason:
          "No clear primary signal was supplied. Define the commercial metric, journey stage, segment and evidence before choosing a tactic.",
      });
    }

    const frameworkIds = ["metric_on_fire", "buy_trifecta", "m_trilogy", "pecti"];
    const frameworks = frameworkIds
      .map((id) => index.frameworks.find((item) => item.id === id))
      .filter(Boolean);
    return success(
      "Prepared a preliminary diagnostic direction. This is not a finding or performance claim; validate it against the brand's first-party data.",
      {
        priorities: priorities.slice(0, 4),
        frameworks,
        next_service_id: "cro_insights",
        human_validation_required: true,
      },
      priorities.length,
    );
  };

  const promptMatchScore = (prompt, situation) => {
    const needle = normaliseText(situation);
    const tokens = needle
      .split(" ")
      .filter((token) => token.length > 1 && !STOPWORDS.has(token))
      .slice(0, 30);
    const fields = [
      [prompt.conversational_prompt, 7],
      [prompt.search_query_variant, 7],
      [prompt.trigger, 5],
      [prompt.desired_progress, 5],
    ];
    let total = 0;
    let exact = false;
    for (const [value, weight] of fields) {
      const field = normaliseText(value);
      if (field === needle) {
        exact = true;
        total = Math.max(total, 100_000);
        continue;
      }
      if (needle.length > 10 && (field.includes(needle) || needle.includes(field))) {
        total += 40 * weight;
      }
      const fieldTokens = new Set(field.split(" "));
      const overlap = tokens.filter((token) => fieldTokens.has(token)).length;
      total += overlap * weight;
    }
    return { total, exact };
  };

  const matchBuyerJob = (index) => async (args) => {
    const situation = cleanString(args.situation, 600);
    if (!situation) throw new TypeError("A situation or question is required.");
    const limit = boundedLimit(args.limit, 3, 3);
    const contentLimit = boundedLimit(args.content_limit, 4, 5);
    const situationTokens = normaliseText(situation)
      .split(" ")
      .filter((token) => token.length > 1 && !STOPWORDS.has(token));
    const hintBoosts = new Map();
    for (const [jobId, pattern, boost] of BUYER_JOB_HINTS) {
      if (pattern.test(situation)) {
        hintBoosts.set(jobId, Math.max(hintBoosts.get(jobId) || 0, boost));
      }
    }
    const scoredPrompts = index.buyer_prompts.map((prompt) => ({
      prompt,
      ...promptMatchScore(prompt, situation),
    }));
    const candidates = index.buyer_jobs
      .map((job) => {
        const prompts = scoredPrompts
          .filter((entry) => entry.prompt.job_id === job.id)
          .sort(
            (a, b) =>
              Number(b.exact) - Number(a.exact) ||
              b.total - a.total ||
              a.prompt.id.localeCompare(b.prompt.id),
          );
        const jobTokens = new Set(
          normaliseText(`${job.name} ${job.family}`).split(" "),
        );
        const jobTokenOverlap = situationTokens.filter((token) =>
          jobTokens.has(token),
        ).length;
        return {
          job,
          prompts,
          exact: Boolean(prompts[0]?.exact),
          relevance:
            (prompts[0]?.total || 0) +
            (hintBoosts.get(job.id) || 0) +
            jobTokenOverlap * 12,
        };
      })
      .sort(
        (a, b) =>
          Number(b.exact) - Number(a.exact) ||
          b.relevance - a.relevance ||
          a.job.id.localeCompare(b.job.id),
      );
    const topCandidate = candidates[0];
    const secondCandidate = candidates[1];
    const exactMatch = Boolean(topCandidate?.exact);
    const minimumMet =
      exactMatch ||
      (situationTokens.length >= 2 && (topCandidate?.relevance || 0) >= 25);
    const ambiguous =
      minimumMet &&
      !exactMatch &&
      Boolean(secondCandidate) &&
      topCandidate.relevance - secondCandidate.relevance <
        Math.max(12, topCandidate.relevance * 0.12);
    const selectedCandidates = !minimumMet
      ? []
      : candidates.slice(0, exactMatch || !ambiguous ? 1 : limit);
    const matches = selectedCandidates.map(({ job, prompts, exact }) => {
      const mappedRecords = index.content.filter((item) =>
        item.buyer_job_ids.includes(job.id),
      );
      const mappedContent = ranked(
        mappedRecords,
        exact ? `${job.name} ${job.family}` : situation,
        ["title", "summary", "canonical_url"],
        contentLimit,
        true,
      );
      return {
        job_id: job.id,
        job_name: job.name,
        job_family: job.family,
        match_basis: exact
          ? "exact_approved_prompt"
          : ambiguous
            ? "ambiguous_public_safe_match"
            : "best_public_safe_match",
        representative_prompts: prompts.slice(0, 3).map(({ prompt }) => ({
          id: prompt.id,
          conversational_prompt: prompt.conversational_prompt,
          search_query_variant: prompt.search_query_variant,
          trigger: prompt.trigger,
          desired_progress: prompt.desired_progress,
        })),
        webmcp_routes: job.webmcp_routes,
        candidate_service_ids: job.candidate_service_ids,
        service_fit_note:
          "Candidate service ids are non-binding routing signals. Use recommend_blend_service with platform, revenue, traffic and scope before recommending a service.",
        mapped_content: mappedContent.map(compactContent),
        content_mapping_note:
          "Mapped content is a lexical ranking within heuristic Buyer Job tags, not an authoritative classification.",
        human_validation_required: true,
      };
    });
    return success(
      matches.length
        ? `Matched ${matches.length} governed Blend Buyer Job${matches.length === 1 ? "" : "s"}. Treat the match as routing guidance and validate the brand-specific diagnosis.`
        : "No governed Blend Buyer Job matched strongly enough. Ask for the store symptom, trigger and desired progress.",
      withEnquiryOffer({
        count: matches.length,
        matches,
        needs_clarification: !matches.length || ambiguous,
        clarification_prompt:
          !matches.length || ambiguous
            ? "What changed, which journey stage or metric is affected, and what progress are you trying to make?"
            : null,
      }, !ambiguous && matches.some((match) => match.job_id === 'JTBD-20')),
      matches.length,
    );
  };

  const readablePageText = (value) =>
    String(value || "")
      .replace(/\u00a0/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const readBlendContent = (index) => async (args, options = {}) => {
    const contentId = cleanString(args.content_id, 300);
    const record = index.content.find((item) => item.id === contentId);
    if (!record) throw new TypeError("Unknown or excluded content id.");
    const url = new URL(canonicalUrl(record.canonical_url));
    const pageUrl = new URL(window.location.href);
    const isShopifyPreview = /\.shopifypreview\.com$/i.test(pageUrl.hostname);
    let requestUrl = url;
    if (url.origin !== pageUrl.origin) {
      if (!isShopifyPreview || url.origin !== CANONICAL_ORIGIN) {
        throw new TypeError("Indexed content is not available on this origin.");
      }
      requestUrl = new URL(`${url.pathname}${url.search}`, pageUrl.origin);
    }
    if (typeof window.DOMParser !== "function") {
      throw new TypeError("Readable HTML extraction is unavailable.");
    }
    const controller = new AbortController();
    const cancel = () => controller.abort();
    options.signal?.addEventListener('abort', cancel, { once: true });
    if (options.signal?.aborted) controller.abort();
    const timeout = window.setTimeout(
      () => controller.abort(),
      CONTENT_FETCH_TIMEOUT_MS,
    );
    try {
      const response = await window.fetch(requestUrl.href, {
        method: "GET",
        credentials: "omit",
        cache: "force-cache",
        redirect: "error",
        referrerPolicy: "no-referrer",
        headers: { Accept: "text/html" },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error("Indexed content request failed.");
      const responseUrl = response.url
        ? new URL(response.url).href
        : requestUrl.href;
      if (responseUrl !== requestUrl.href) {
        throw new Error("Indexed content redirected.");
      }
      const contentType = String(response.headers.get("content-type") || "");
      const contentLength = Number(response.headers.get("content-length") || 0);
      if (!/^text\/html\b/i.test(contentType) || contentLength > MAX_PAGE_BYTES) {
        throw new Error("Indexed content response is not approved HTML.");
      }
      const html = await response.text();
      if (html.length > MAX_PAGE_BYTES) {
        throw new Error("Indexed content response is larger than expected.");
      }
      const parsed = new window.DOMParser().parseFromString(html, "text/html");
      if (!parsed || parsed.querySelector("parsererror")) {
        throw new Error("Indexed content could not be parsed.");
      }
      for (const element of parsed.querySelectorAll(
        'script,style,noscript,nav,footer,form,template,svg,iframe,aside,dialog,[role="dialog"],agency-contact-drawer,.blend-newsletter-popup,[id*="__related_articles"],.section-scrolling-content',
      )) {
        element.remove();
      }
      // Case studies, A/B tests and service pages are composed of multiple
      // Shopify sections. Their first <article> may belong to a global popup.
      // Resolve the main landmark before considering an article body.
      const main = parsed.querySelector('main, [role="main"]');
      const root = main?.querySelector('#main-content, .article-template__content, [itemprop="articleBody"]') || main || parsed.querySelector('article.article-template, article[itemtype$="/Article"]');
      if (!root) throw new Error("Indexed content has no readable body.");
      const fullText = readablePageText(root.textContent);
      if (!fullText) throw new Error("Indexed content has no readable text.");
      const headings = [...root.querySelectorAll("h1,h2,h3")]
        .map((heading) => readablePageText(heading.textContent).slice(0, 240))
        .filter(Boolean)
        .slice(0, MAX_HEADINGS);
      const pageHeading = readablePageText(
        parsed.querySelector("h1")?.textContent || parsed.title || record.title,
      ).slice(0, 500);
      const offset = args.offset || 0;
      if (offset >= fullText.length && offset !== 0) throw new TypeError("offset is beyond this content. Start at zero or use the returned next_offset.");
      const nextOffset = offset + MAX_CONTENT_TEXT < fullText.length ? offset + MAX_CONTENT_TEXT : null;
      const truncated = nextOffset != null;
      const text = fullText.slice(offset, offset + MAX_CONTENT_TEXT);
      return success(
        `Read approved public Blend content “${record.title}”.${truncated ? " More content remains; call again with next_offset to read the continuation and any later qualifications." : ""}`,
        {
          id: record.id,
          title: pageHeading || record.title,
          url: record.canonical_url,
          headings,
          text,
          truncated,
          offset,
          next_offset: nextOffset,
          total_characters: fullText.length,
        },
        1,
      );
    } finally {
      window.clearTimeout(timeout);
      options.signal?.removeEventListener('abort', cancel);
    }
  };

  const contactDrawer = () => {
    const drawer = document.querySelector('agency-contact-drawer');
    if (!drawer || typeof drawer.getWebMcpState !== 'function') throw new Error('The contact drawer is not ready. Please retry shortly.');
    return drawer;
  };

  const enquiryResult = (result, prepared = false) => {
    const approvalRequest = result.ready_to_submit && result.review_token && !result.submitted && result.submission_status === 'not_submitted' ? {
      status: 'awaiting_visitor_approval',
      recipient: 'Blend Commerce',
      contact_url: `${CANONICAL_ORIGIN}/pages/contact-us`,
      answers: result.fields.map(({ field, question, value }) => ({ field, question, value })),
      attachments: result.attachments,
      attached_document_previews: (result.generated_documents || []).filter((document) => document.attached),
      question: result.attachments.length ? 'Send this enquiry and the listed attachments to Blend Commerce now?' : 'Send this enquiry to Blend Commerce now?',
      effect: 'Sends these answers and any listed attachments through Blend\u2019s existing contact form service. Blend may respond to the enquiry. This does not book a call or grant marketing consent.',
      choices: ['Send enquiry', 'Edit draft', 'Keep as draft'],
      requires_explicit_confirmation: true,
    } : null;
    const reviewedResult = {
      ...result,
      approval_request: approvalRequest,
      ...(approvalRequest ? { next_step: 'Show the visitor all approval_request answers, attachments, full attached_document_previews, recipient and effect, then ask its question. Wait for explicit agreement to send this exact draft before calling submit_blend_enquiry with the current review_token and confirmed=true. If answers or documents change, review the revised draft again.' } : {}),
    };
    const response = success(
      result.submitted ? 'The enquiry service accepted this enquiry. Downstream CRM and email delivery are not independently confirmed by the page.'
        : prepared ? 'The contact drawer is open and the supplied answers are prepared for review. Nothing has been submitted.'
        : 'Current contact enquiry state. Nothing is sent by reading or preparing this form.',
      reviewedResult,
      result.updated_fields?.length || 0,
    );
    if (result.submission_status === 'unknown') {
      response.isError = true;
      response.content[0].text = 'The enquiry delivery is uncertain. Do not automatically retry or claim it was delivered.';
    }
    return response;
  };

  const prepareEnquiry = () => async (args) => enquiryResult(await contactDrawer().prepareWebMcpEnquiry(args), true);

  const BRIEF_SECTIONS = [
    ['brand_name', 'Brand', 'What is the brand called?'],
    ['website_url', 'Store URL', 'What is the store URL?'],
    ['platform', 'Platform', 'Which ecommerce platform do you use?'],
    ['annual_revenue', 'Annual revenue and currency', 'What is the annual revenue and currency?'],
    ['monthly_sessions', 'Monthly sessions', 'Approximately how many monthly sessions does the store receive?'],
    ['problem_in_own_words', 'The problem, in the prospect’s own words', 'How would you describe the problem in your own words?'],
    ['desired_outcome', 'Desired outcome', 'What outcome would make this work worthwhile?'],
    ['already_tried', 'What has already been tried', 'What have you already tried, and what happened?'],
    ['available_data', 'Available data', 'What analytics, research or test results are available?'],
    ['implementation_owner', 'Who can implement changes', 'Who could implement changes: your team, Blend, or a combination?'],
    ['mapping_call_goals', 'What is needed from the mapping call', 'What would you like to leave the mapping call knowing?'],
    ['constraints', 'Constraints, timing and dependencies', 'Are there any deadlines, constraints or dependencies?'],
    ['notes', 'Additional context', null],
  ];
  const suppliedText = (value) => typeof value === 'string' && value.trim() ? value : 'Not supplied — to confirm.';
  const draftSection = (title, value) => `## ${title}\n\n${suppliedText(value)}`;
  const documentResult = (enquiry, id) => {
    const result = enquiryResult(enquiry);
    result.content[0].text = 'Prepared a local document draft for review. Nothing has been sent, no CRM record has been created, and no marketing consent has been given.';
    result.structuredContent = { ...result.structuredContent, prepared_document_id: id, document_review_required: true, storage: 'current_page_memory_only', transmitted: false };
    return result;
  };
  const prepareProjectBrief = async ({ attach_to_enquiry, ...changes }) => {
    const drawer = contactDrawer();
    const previous = drawer.webMcpDocuments?.get('project_brief');
    const formWebsite = drawer.webMcpFields().find((field) => field.key === 'website_url')?.value;
    const data = { ...(formWebsite ? { website_url: formWebsite } : {}), ...previous?.data, ...changes };
    const missingQuestions = BRIEF_SECTIONS.filter(([key, , question]) => question && !data[key]?.trim()).map(([field, , question]) => ({ field, question }));
    const text = [
      '# Project brief — draft for review',
      'Prepared from information supplied by the prospect. Missing details are marked for confirmation. This is not an agreed scope, a diagnosis or a promise of results.',
      ...BRIEF_SECTIONS.map(([key, title]) => draftSection(title, data[key])),
      '## Questions still to confirm\n\n' + (missingQuestions.length ? missingQuestions.map(({ question }) => `- ${question}`).join('\n') : 'No blank brief fields. Confirm the accuracy of the supplied information.'),
      '## Sending boundary\n\nThis document stays in this browser page until downloaded or included with an explicitly approved enquiry. Preparing it does not send an enquiry, create a CRM record, book a call or subscribe anyone to marketing.',
    ].join('\n\n') + '\n';
    return documentResult(await drawer.prepareWebMcpDocument({ id: 'project_brief', title: 'Project brief', text, data, attached: attach_to_enquiry ?? previous?.attached ?? true, missingQuestions }), 'project_brief');
  };

  const prepareInternalDecisionPack = (index) => async ({ attach_to_enquiry, ...changes }) => {
    const drawer = contactDrawer();
    const previous = drawer.webMcpDocuments?.get('internal_decision_pack');
    const brief = drawer.webMcpDocuments?.get('project_brief');
    const data = { ...previous?.data, ...changes };
    const problem = data.problem_in_own_words ?? brief?.data.problem_in_own_words;
    const outcome = data.desired_outcome ?? brief?.data.desired_outcome;
    const services = (data.service_ids || []).map((id) => index.services.find((service) => service.id === id));
    const cases = (data.case_study_ids || []).map((id) => index.content.find((record) => record.id === id && record.type === 'case_study'));
    if (services.some((record) => !record) || cases.some((record) => !record)) throw new TypeError('Use service and case study IDs from Blend’s approved public tools.');
    const questions = [
      ['audience', 'Who needs to review this decision?', data.audience],
      ['problem_in_own_words', 'What is the problem in the prospect’s own words?', problem],
      ['desired_outcome', 'What outcome does the prospect want?', outcome],
      ['decision_needed', 'What decision is the internal team being asked to make?', data.decision_needed],
      ['proposed_scope', 'What scope should be discussed with Blend?', data.proposed_scope],
      ['internal_involvement', 'Who owns access, approvals, implementation and QA internally?', data.internal_involvement || brief?.data.implementation_owner],
      ['commercial_assumptions', 'What budget, timing or commercial assumptions need confirmation?', data.commercial_assumptions],
    ].filter(([, , value]) => !value?.trim()).map(([field, question]) => ({ field, question }));
    const frameworks = index.frameworks.filter((framework) => ['buy_trifecta', 'pecti', 'metric_on_fire'].includes(framework.id));
    const journey = ['cro_insights', 'cro_implementation'].map((id) => index.services.find((service) => service.id === id));
    const text = [
      '# Internal decision pack — draft for buyer review',
      'A discussion aid assembled from prospect-supplied context and Blend’s approved public information. Proposed scope and commercial assumptions are unagreed. This is not a bespoke strategy, quotation, contract or forecast.',
      draftSection('Intended audience', data.audience),
      draftSection('Decision requested', data.decision_needed),
      ...(brief ? [draftSection(`Prospect context (copied from project brief revision ${brief.revision})`, [brief.data.brand_name, brief.data.website_url, brief.data.platform, brief.data.annual_revenue].filter(Boolean).join(' | '))] : []),
      draftSection('Problem — prospect supplied', problem),
      draftSection('Desired outcome — prospect supplied', outcome),
      draftSection('Proposed scope — prospect supplied; subject to agreement', data.proposed_scope),
      '## Public service information for evaluation\n\n' + (services.length ? services.map((service) => `### ${service.title}\n\n${service.summary}\n\nPublished deliverables:\n${service.deliverables.map(item => `- ${item}`).join('\n')}\n\nSource: ${service.canonical_url}`).join('\n\n') : 'No service has been selected. Confirm fit and scope with Blend.'),
      '## How Blend prioritises recommendations\n\n' + frameworks.map((framework) => `### ${framework.title}\n\n${framework.summary}\n${framework.components.map(({ name, meaning }) => `${name}: ${meaning}`).join('; ')}${framework.scoring_note ? `\n${framework.scoring_note}` : ''}\nSource: ${framework.canonical_url}`).join('\n\n'),
      '## From investigation to implementation — published approach\n\n' + journey.map((service) => `### ${service.title}\n\n${service.summary}\n\nSource: ${service.canonical_url}`).join('\n\n') + '\nThese describe available services; they do not commit either party to this sequence or scope. Confirm the route after reviewing the problem and available evidence.',
      draftSection('Internal involvement — supplied expectations, to confirm', data.internal_involvement ?? brief?.data.implementation_owner),
      '## Internal planning questions\n\nIdentify an owner for analytics and research access, commercial priorities and approvals, implementation and release QA. Confirm which responsibilities sit with the internal team and which sit with Blend.',
      draftSection('Commercial assumptions — prospect supplied; unconfirmed', data.commercial_assumptions),
      `## Published commercial context\n\nBlend’s main services start from US$${index.business_rules.main_service_starting_price_usd.toLocaleString('en-US')}; smaller engagements may be available. This is an indicative public starting point, not a quote for this project. Confirm fee basis, scope, taxes, third-party costs, timing and responsibilities in a proposal. No return, uplift or payback is guaranteed.\nSource: ${CANONICAL_ORIGIN}/pages/our-services`,
      '## Selected public evidence\n\n' + (cases.length ? cases.map((record) => `### ${record.title}\n\nBlend’s indexed public summary: ${record.summary}\nSource: ${record.canonical_url}${record.last_modified ? `\nSource last modified: ${record.last_modified}` : ''}`).join('\n\n') + '\nPast outcomes are context for evaluation, not predicted results for this prospect. Review the linked cases and their conditions before using any figures.' : 'No case studies selected. Use find_case_studies to choose relevant approved evidence; do not invent examples or results.'),
      draftSection('Additional unresolved questions — prospect supplied', data.open_questions),
      '## Outstanding decisions\n\n' + [...questions.map(({ question }) => `- ${question}`), '- Confirm scope, feasibility, responsibilities, fees and timeline with Blend.', '- Agree measurement baselines and success criteria before forecasting outcomes.'].join('\n'),
      `## Source basis\n\nBlend’s approved public information index, generated ${index.generated_at}. Source dates describe the indexed evidence, not a new independent verification of every claim.`,
      '## Sharing boundary\n\nThis pack is for the prospect’s own review. It is not included with an enquiry unless chosen explicitly. Preparing or downloading it does not send an enquiry, create a CRM record or grant marketing consent.',
    ].join('\n\n') + '\n';
    return documentResult(await drawer.prepareWebMcpDocument({ id: 'internal_decision_pack', title: 'Internal decision pack', text, data, attached: attach_to_enquiry ?? previous?.attached ?? false, missingQuestions: questions, sourceBriefRevision: brief?.revision }), 'internal_decision_pack');
  };

  const bookingUrl = () => {
    const drawer = contactDrawer();
    const frame = drawer.querySelector('iframe[data-booking-src], iframe[src*="calendly.com/"]');
    const fallback = drawer.querySelector('[data-scheduler-fallback-url]');
    const raw = frame?.dataset.bookingSrc || frame?.getAttribute('src') || fallback?.dataset.schedulerFallbackUrl;
    if (!raw) throw new Error('The booking calendar is unavailable.');
    const url = new URL(raw);
    if (url.protocol !== 'https:' || url.hostname !== 'calendly.com' || url.username || url.password) throw new Error('The booking destination is invalid.');
    return `${url.origin}${url.pathname}`;
  };

  const contactToolDefinitions = () => [
    {
      name: 'prepare_project_brief',
      title: 'Prepare project brief for review',
      description: 'Prepare a local, downloadable project brief when the visitor asks for one or agrees to reuse their conversation details. Capture their problem in their own words, outcome, previous attempts, available data, implementation owner and mapping-call needs. Preserve omitted answers; empty strings clear optional draft details. Ask only the returned missing questions, and never invent facts. A UTF-8 Markdown (.md) file is staged with the enquiry by default for review; attach_to_enquiry=false keeps it for the visitor only. Show the document and enquiry together before a separate approved submit_blend_enquiry call. Preparing creates no CRM record, sends nothing and gives no marketing consent. Drafts last only while this page remains open.',
      annotations: { readOnlyHint: false, consequentialHint: false, idempotentHint: true, untrustedContentHint: true },
      inputSchema: objectSchema({
        ...Object.fromEntries(BRIEF_SECTIONS.map(([key, title]) => [key, { type: 'string', maxLength: ['brand_name', 'platform'].includes(key) ? 150 : ['website_url', 'annual_revenue', 'monthly_sessions'].includes(key) ? 500 : 2000, description: `${title}. Use only visitor-supplied details; leave unknown information out.` }])),
        attach_to_enquiry: { type: 'boolean', default: true, description: 'Stage the full brief as a file with the draft enquiry. Nothing is sent until the visitor approves the exact enquiry and attachments.' },
      }),
      execute: prepareProjectBrief,
    },
    {
      name: 'open_blend_contact',
      title: 'Open Blend contact form',
      description: 'Open Blend Commerce’s contact drawer directly at the enquiry form. Preserves all existing answers. Returns the current questions, choices and missing fields. Does not send an enquiry.',
      annotations: { readOnlyHint: false, consequentialHint: false, idempotentHint: true },
      inputSchema: objectSchema({}),
      execute: async () => {
        const drawer = contactDrawer();
        await drawer.openWebMcpRoute('form');
        return enquiryResult(drawer.getWebMcpState());
      },
    },
    {
      name: 'get_blend_enquiry_state',
      title: 'Read Blend enquiry state',
      description: 'Read the current Blend contact form answers, available choices, missing fields, attachment names and submission status. Use only when helping the visitor with their enquiry. When ready, approval_request supplies the complete draft, recipient and approval question to show the visitor. A review token binds submission to these exact answers. Does not send or change form fields.',
      annotations: { readOnlyHint: true, untrustedContentHint: true },
      inputSchema: objectSchema({}),
      execute: async () => enquiryResult(contactDrawer().getWebMcpState()),
    },
    {
      name: 'prepare_blend_enquiry',
      title: 'Prepare Blend enquiry',
      description: 'Prepare an enquiry when a visitor asks to contact Blend or accepts an offer to prepare a draft. Open and fill the contact drawer using only supplied answers; preserve omitted answers and ask only for missing required fields. Supports multiple challenges and Other answers. Use get_blend_enquiry_state for current choices. When ready, show the complete approval_request and ask its question in the conversation. Wait for explicit approval before submitting with the current review token. Preparing never sends, subscribes, gives consent or books a call.',
      annotations: { readOnlyHint: false, consequentialHint: false, idempotentHint: true, untrustedContentHint: true },
      inputSchema: objectSchema({
        first_name: { type: 'string', minLength: 1, maxLength: 120 },
        email: { type: 'string', minLength: 3, maxLength: 254, description: 'Use the visitor’s real reply address. Reserved placeholder domains such as example.com are rejected. For an explicitly authorised live test, use an authorised test inbox or the email provider’s documented test recipient.' },
        website_url: { type: 'string', minLength: 3, maxLength: 500 },
        growth_objective: { type: 'array', minItems: 1, maxItems: 5, uniqueItems: true, items: { type: 'string', minLength: 1, maxLength: 180 }, description: 'Use current choice labels from get_blend_enquiry_state. One unmatched answer is stored as Other.' },
        cro_position: { type: 'string', minLength: 1, maxLength: 180 },
        sessions: { type: 'string', minLength: 1, maxLength: 180, description: 'The visitor’s monthly traffic range; use a current choice label or an Other answer.' },
        timeline: { type: 'string', minLength: 1, maxLength: 180 },
        message: { type: 'string', maxLength: 1200 },
      }),
      execute: prepareEnquiry(),
    },
    {
      name: 'submit_blend_enquiry',
      title: 'Send reviewed enquiry to Blend',
      description: 'Send the prepared enquiry and any visitor-attached files to Blend Commerce through the contact form’s existing enquiry service. Call only after the visitor explicitly agrees to send these exact answers and attachments to Blend. Supply the current review_token from get_blend_enquiry_state or prepare_blend_enquiry and confirmed=true. Reuses normal form validation. An accepted receipt confirms endpoint acceptance, not downstream CRM or email delivery. Never automatically retry an uncertain submission. Does not book a call or grant marketing consent.',
      annotations: { readOnlyHint: false, consequentialHint: true, idempotentHint: true, openWorldHint: true, untrustedContentHint: true },
      inputSchema: objectSchema({
        confirmed: { type: 'boolean', const: true, description: 'True only after the visitor explicitly agrees to send the reviewed enquiry to Blend.' },
        review_token: { type: 'string', minLength: 1, maxLength: 100, description: 'The current token for the exact reviewed answers and attachments.' },
      }, ['confirmed', 'review_token']),
      execute: async (args, options) => enquiryResult(await contactDrawer().submitWebMcpEnquiry(args, options)),
    },
    {
      name: 'open_blend_booking',
      title: 'Open CRO Mapping Call booking',
      description: 'Open Blend’s existing Calendly booking drawer for a CRO Mapping Call. Returns the public booking link for the visitor. The Calendly frame is cross-origin: this tool cannot read available times, choose a time or confirm a booking. The visitor completes scheduling in Calendly. No personal information is prefilled or sent by this tool.',
      annotations: { readOnlyHint: false, consequentialHint: false, idempotentHint: true, openWorldHint: true },
      inputSchema: objectSchema({}),
      execute: async () => {
        const url = bookingUrl();
        await contactDrawer().openWebMcpRoute('calendar');
        return success('Opened the Calendly booking drawer. Complete date selection and booking in Calendly; no call has been booked by this tool.', { open: true, provider: 'Calendly', booking_url: url, booked: false, manual_completion_required: true, can_read_availability: false, can_book: false });
      },
    },
  ];

  const objectSchema = (properties, required = []) => ({
    $schema: JSON_SCHEMA,
    type: "object",
    additionalProperties: false,
    properties,
    ...(required.length ? { required } : {}),
  });

  const limitSchema = (maximum, fallback) => ({
    type: "integer",
    minimum: 1,
    maximum,
    default: fallback,
  });

  const querySchema = {
    type: "string",
    maxLength: MAX_QUERY_LENGTH,
    description: "Plain-language search terms. Do not include personal information.",
  };

  const toolDefinitions = (index) => [
    ...contactToolDefinitions(),
    {
      name: 'prepare_internal_decision_pack',
      title: 'Prepare internal decision pack',
      description: 'Prepare a downloadable Markdown (.md) draft for the prospect’s internal decision makers, using their supplied context, the current project brief and approved public Blend service, methodology and case-study records. Cover the problem, proposed scope, evidence, internal involvement, commercial assumptions and unresolved questions. Label proposed scope and assumptions as unagreed; never invent a bespoke strategy, guaranteed return or agreed terms. Use service IDs and case-study IDs from the public tools. Omitted answers are preserved; empty strings clear them. The pack stays for the visitor’s own use by default. Attach it to an enquiry only if the visitor asks, using attach_to_enquiry=true; sending still requires separate review and approval. No enquiry, CRM record, booking or subscription is created by preparation.',
      annotations: { readOnlyHint: false, consequentialHint: false, idempotentHint: true, untrustedContentHint: true },
      inputSchema: objectSchema({
        ...Object.fromEntries(['audience', 'decision_needed', 'problem_in_own_words', 'desired_outcome', 'proposed_scope', 'internal_involvement', 'commercial_assumptions', 'open_questions'].map((key) => [key, { type: 'string', maxLength: 2000, description: 'Visitor-supplied information. Leave unknown details out.' }])),
        service_ids: { type: 'array', maxItems: 3, uniqueItems: true, items: { type: 'string', enum: index.services.map((service) => service.id) }, description: 'Approved services to explain as options for evaluation, not as agreed scope.' },
        case_study_ids: { type: 'array', maxItems: 3, uniqueItems: true, items: { type: 'string', enum: index.content.filter((record) => record.type === 'case_study').map((record) => record.id) }, description: 'Select relevant approved public evidence. Omit if no suitable case has been chosen.' },
        attach_to_enquiry: { type: 'boolean', default: false, description: 'True only when the visitor wants this internal pack included with their enquiry for later approval.' },
      }),
      execute: prepareInternalDecisionPack(index),
    },
    {
      name: "find_blend_content",
      description:
        "Search Blend Commerce's approved public pages, Shopify CRO articles, newsletter archive, case studies, A/B tests and resources. Returns a small set of canonical Blend URLs and does not fetch arbitrary pages.",
      annotations: { readOnlyHint: true },
      inputSchema: objectSchema({
        query: querySchema,
        type: { type: "string", enum: CONTENT_TYPES },
        buyer_job_id: { type: "string", enum: BUYER_JOB_IDS },
        limit: limitSchema(8, 5),
      }),
      execute: findContent(index),
    },
    {
      name: "find_case_studies",
      description:
        "Find approved public Blend Commerce Shopify case studies by commercial problem, metric or topic. Results are evidence links, not guarantees that another brand will get the same outcome." + ENQUIRY_OFFER_GUIDANCE,
      annotations: { readOnlyHint: true },
      inputSchema: objectSchema({
        query: querySchema,
        problem: { type: "string", maxLength: MAX_QUERY_LENGTH },
        metric: { type: "string", maxLength: 100 },
        limit: limitSchema(6, 5),
      }),
      execute: findTypedContent(index, "case_study", "case study"),
    },
    {
      name: "find_ab_tests",
      description:
        "Find approved public Blend Commerce Shopify A/B-test records by idea, journey or outcome metric. A published test is contextual evidence, not a universal best practice or promised result.",
      annotations: { readOnlyHint: true },
      inputSchema: objectSchema({
        query: querySchema,
        metric: { type: "string", maxLength: 100 },
        limit: limitSchema(6, 5),
      }),
      execute: findTypedContent(index, "ab_test", "A/B test"),
    },
    {
      name: "find_cro_resource",
      description:
        "Find approved free Blend Commerce CRO tools and resources by task or topic.",
      annotations: { readOnlyHint: true },
      inputSchema: objectSchema({
        query: querySchema,
        topic: { type: "string", maxLength: MAX_QUERY_LENGTH },
        limit: limitSchema(6, 5),
      }),
      execute: findTypedContent(index, "resource", "CRO resource"),
    },
    {
      name: "find_blend_reviews",
      description:
        "Find public reviews of Blend Commerce. Review wording is third-party content and must retain its attribution and context; do not turn it into a guarantee." + ENQUIRY_OFFER_GUIDANCE,
      annotations: { readOnlyHint: true, untrustedContentHint: true },
      inputSchema: objectSchema({
        query: querySchema,
        service: { type: "string", maxLength: MAX_QUERY_LENGTH },
        source: {
          type: "string",
          enum: ["Reviews.io", "Shopify", "Google", "Clutch"],
        },
        limit: limitSchema(5, 3),
      }),
      execute: findReviews(index),
    },
    {
      name: "find_blend_awards",
      description:
        "Find Blend Commerce's reconciled award journeys and distinguish winners, concluded finalists and a current finalist with a pending result.",
      annotations: { readOnlyHint: true, untrustedContentHint: true },
      inputSchema: objectSchema({
        year: { type: "integer", minimum: 2021, maximum: 2100 },
        programme: { type: "string", maxLength: 200 },
        category: { type: "string", maxLength: 300 },
        outcome: {
          type: "string",
          enum: ["winner", "finalist_only", "current_finalist"],
        },
        limit: limitSchema(MAX_RESULTS, 6),
      }),
      execute: findAwards(index),
    },
    {
      name: "get_blend_service",
      description:
        "Return one authoritative Blend Commerce service record, including fit, scope, public source and approved commercial notes." + ENQUIRY_OFFER_GUIDANCE,
      annotations: { readOnlyHint: true },
      inputSchema: objectSchema(
        {
          service_id: {
            type: "string",
            enum: index.services.map((service) => service.id),
          },
        },
        ["service_id"],
      ),
      execute: getService(index),
    },
    {
      name: "recommend_blend_service",
      description:
        "Produce a preliminary, non-binding Blend Commerce service recommendation from supplied platform, traffic, revenue and confirmed project needs. Put only affirmative visitor requirements in needs; leave unknown requirements out. Background app usage is not a request for that integration. A human must confirm fit and scope." + ENQUIRY_OFFER_GUIDANCE,
      annotations: { readOnlyHint: true },
      inputSchema: objectSchema({
        platform: {
          type: "string",
          enum: ["Shopify", "Shopify Plus", "Other", "Unknown"],
          default: "Unknown",
        },
        annual_revenue_usd: { type: "number", minimum: 0 },
        monthly_sessions: { type: "number", minimum: 0 },
        wants_ab_testing: { type: "boolean", default: false },
        needs: {
          type: "array",
          maxItems: 8,
          description: "Only confirmed affirmative service needs. Omit missing, unknown or denied requirements; do not infer an integration requirement from an installed app.",
          items: { type: "string", maxLength: 80 },
        },
        project_scope: { type: "string", maxLength: 500 },
      }),
      execute: recommendService(index),
    },
    {
      name: "diagnose_growth_priority",
      description:
        "Use Blend's Metric on Fire, Buy Trifecta, Messaging-Merchandising-Mechanics and PECTI frameworks to suggest what to investigate first. This is a diagnostic direction, not a finding or forecast.",
      annotations: { readOnlyHint: true },
      inputSchema: objectSchema({
        known_problem: { type: "string", maxLength: 600 },
        conversion_rate_signal: {
          type: "string",
          enum: ["low", "stable", "high", "unknown"],
          default: "unknown",
        },
        aov_signal: {
          type: "string",
          enum: ["low", "stable", "high", "unknown"],
          default: "unknown",
        },
        repeat_purchase_signal: {
          type: "string",
          enum: ["low", "stable", "high", "unknown"],
          default: "unknown",
        },
        traffic_trend: {
          type: "string",
          enum: ["down", "stable", "up", "unknown"],
          default: "unknown",
        },
        data_confidence: {
          type: "string",
          enum: ["low", "mixed", "high", "unknown"],
          default: "unknown",
        },
      }),
      execute: diagnosePriority(index),
    },
    {
      name: "match_blend_buyer_job",
      description:
        "Match a natural-language Shopify growth situation to Blend's governed 24-job, 96-prompt Buyer Job universe. Returns bounded public-safe prompts, desired progress, related content, governed routes and non-binding candidate service ids; it is routing guidance, not a store diagnosis or service recommendation.",
      annotations: { readOnlyHint: true, openWorldHint: false },
      inputSchema: objectSchema(
        {
          situation: {
            type: "string",
            minLength: 3,
            maxLength: 600,
            description:
              "The visitor's Shopify growth situation or question. Do not include personal information.",
          },
          limit: limitSchema(3, 3),
          content_limit: limitSchema(5, 4),
        },
        ["situation"],
      ),
      execute: matchBuyerJob(index),
    },
    {
      name: "read_blend_content",
      description:
        "Read the public body of one exact approved Blend content record by indexed id. Fetches only that canonical same-origin page, strips non-content elements and returns bounded plain text; arbitrary or excluded URLs are unreachable.",
      annotations: {
        readOnlyHint: true,
        openWorldHint: true,
        untrustedContentHint: true,
      },
      inputSchema: objectSchema(
        {
          content_id: {
            type: "string",
            minLength: 3,
            maxLength: 300,
            pattern: "^[a-z0-9_-]+$",
            description:
              "An exact approved content id returned by a Blend discovery tool. URLs and arbitrary ids are rejected.",
          },
          offset: { type: "integer", minimum: 0, maximum: MAX_PAGE_BYTES, description: "Start at 0. When next_offset is returned, pass it to read the continuation, including later results and limitations." },
        },
        ["content_id"],
      ),
      execute: readBlendContent(index),
    },
  ];

  const reportStatus = (status, code = '') => {
    const config = document.querySelector(CONFIG_SELECTOR);
    if (!config) return;
    config.dataset.webmcpVersion = VERSION;
    config.dataset.webmcpStatus = status;
    config.dataset.webmcpToolCount = String(state.registered.size);
    config.dataset.webmcpError = code;
  };

  const registerTools = async (index) => {
    const context = modelContext();
    if (!context || state.registrationInProgress || !state.active) return false;
    if (
      !state.registrationController ||
      state.registrationController.signal.aborted
    ) {
      state.registrationController = new AbortController();
    }
    const signal = state.registrationController.signal;
    state.registrationInProgress = true;
    try {
      const definitions = index ? toolDefinitions(index) : contactToolDefinitions();
      for (const definition of definitions) {
        if (!state.active || signal.aborted) break;
        if (state.registered.has(definition.name)) continue;
        const { execute, ...tool } = definition;
        state.registrationContext = context;
        const registration = context.registerTool({
          ...tool,
          execute: wrapped(definition),
        }, { signal });
        await Promise.resolve(registration);
        if (!state.active || signal.aborted) break;
        state.registered.add(definition.name);
      }
      const complete = definitions.every((definition) => state.registered.has(definition.name));
      reportStatus(complete ? (index ? 'ready' : 'contact_ready') : 'registering');
      return complete;
    } finally {
      state.registrationInProgress = false;
    }
  };

  const waitForModelContext = () => {
    if (state.retryTimer != null) window.clearTimeout(state.retryTimer);
    let attempts = 0;
    const attempt = async () => {
      if (!state.active) return;
      attempts += 1;
      if (modelContext()) { void boot(); return; }
      reportStatus('unsupported');
      if (attempts < 30) state.retryTimer = window.setTimeout(attempt, 1000);
    };
    void attempt();
  };

  const abortRegistrations = () => {
    state.active = false;
    // The earlier navigator API unregisters by name; the document API uses AbortSignal.
    if (typeof state.registrationContext?.unregisterTool === 'function') {
      for (const name of state.registered) {
        try { state.registrationContext.unregisterTool(name); } catch (_error) { /* Already removed. */ }
      }
    }
    if (state.retryTimer != null) {
      window.clearTimeout(state.retryTimer);
      state.retryTimer = null;
    }
    if (
      state.registrationController &&
      !state.registrationController.signal.aborted
    ) {
      state.registrationController.abort();
    }
    state.registrationController = null;
    state.registered.clear();
    reportStatus('inactive');
  };

  const bindLifecycle = () => {
    if (state.lifecycleBound) return;
    state.lifecycleBound = true;
    window.addEventListener("pagehide", abortRegistrations);
    window.addEventListener("pageshow", (event) => {
      if (!event.persisted) return;
      state.active = true;
      void boot();
    });
    window.addEventListener('focus', () => {
      if (state.active && modelContext() && (!state.index || state.registered.size < toolDefinitions(state.index).length)) void boot();
    });
  };

  const boot = async () => {
    const config = document.querySelector(CONFIG_SELECTOR);
    if (!config || state.booting || !state.active) return;
    bindLifecycle();
    if (!modelContext()) { waitForModelContext(); return; }
    state.booting = true;
    try {
      await registerTools(null);
      reportStatus('loading_index');
      let lastError;
      for (let attempt = 0; attempt < 3 && state.active; attempt += 1) {
        try {
          const index = await loadIndex(config);
          if (await registerTools(index)) return;
        } catch (error) { lastError = error; }
        if (attempt < 2) await new Promise((resolve) => window.setTimeout(resolve, 1000 * (attempt + 1)));
      }
      if (lastError) throw lastError;
    } catch (error) {
      reportStatus('degraded', state.index ? 'registration_failed' : 'index_unavailable');
      console.warn('[Blend WebMCP] Some tools are unavailable. Check the data-blend-webmcp-config status attributes.');
    } finally {
      state.booting = false;
    }
  };

  void boot();
})();
